import { useCommentModal } from '@/components/comments/context';
import { useReminderModal } from '@/components/reminders/context';
import { useDebounce } from '@/hooks/debounce';
import { usePeopleLookup } from '@/store/people/hooks';
import type { Person } from '@/store/people/types';
import {
  Dropdown,
  DropdownTrigger,
  Button,
  DropdownMenu,
  DropdownItem,
  Popover,
  PopoverTrigger,
  PopoverContent,
  CircularProgress,
  Input,
} from '@heroui/react';
import { useState, useRef, useEffect, useCallback } from 'react';
import { Empty } from '../empty';
import { StandardIcon } from '../icon';
import { PersonItem } from '../output/person';
import { useDrawerActions } from '@/store/drawer';
import { useSpecificPeopleAccess } from '@/store/access/access';
import { MemberStatusChip } from '../output/memberstatus';

type ActionKey = 'reminder' | 'comment';

const actions: { key: ActionKey; icon: string; label: string }[] = [
  {
    key: 'reminder',
    icon: 'mdi:bell-outline',
    label: 'Reminder',
  },
  {
    key: 'comment',
    icon: 'mdi:comment-outline',
    label: 'Comment',
  },
];

export function PeopleQuickSearch({
  className,
  inputClassName,
}: {
  className?: string;
  inputClassName?: string;
}) {
  const [open, setIsOpen] = useState(false);
  const closing = useRef(false);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounce(searchInput, 400);
  const { people, loading, empty } = usePeopleLookup({ search });
  const trigger = useRef<HTMLDivElement | null>(null);
  const {
    setOpen: setReminderModalIsOpen,
    setCurrentPerson: setReminderSubject,
    setCurrentReminder,
  } = useReminderModal();
  const {
    setOpen: setCommentModalIsOpen,
    setCurrentPerson: setCommentSubject,
  } = useCommentModal();
  const drawerActions = useDrawerActions();

  const setOpen = (value: boolean) => {
    setIsOpen(value);
    if (!value) {
      closing.current = true;
      setTimeout(() => (closing.current = false), 200);
      setSearchInput('');
    } else {
      closing.current = false;
    }
  };

  useEffect(() => {
    if (search) {
      setOpen(true);
    }
  }, [search]);

  const onAction = useCallback(
    (key: ActionKey, subject: Person) => {
      if (key === 'reminder') {
        setCurrentReminder(null);
        setReminderSubject(subject);
        setReminderModalIsOpen(true);
        setOpen(false);
      } else if (key === 'comment') {
        setCommentSubject(subject);
        setCommentModalIsOpen(true);
        setOpen(false);
      }
    },
    [
      setReminderSubject,
      setReminderModalIsOpen,
      setCommentModalIsOpen,
      setCommentSubject,
      setCurrentReminder,
    ],
  );

  const onButtonPress = () => {
    if (!closing.current) {
      setOpen(true);
    }
  };

  const onClosePress = () => {
    setOpen(false);
    closing.current = false;
  };

  const onPersonClick = (item: Person) => {
    console.log('person click %s', item.id);
    drawerActions.setCurrentPerson(item);
    drawerActions.setCurrentComment(null);
    drawerActions.setCurrentMention(null);
    drawerActions.setCurrentTab('profile');
    drawerActions.setOpen(true);
    setOpen(false);
  };

  const extraClassNames = open
    ? //TODO: ? 'fixed top-0 left-0 right-0 h-[100px] px-2 py-1 z-20 bg-white'
      ''
    : '';

  const endContent = loading ? (
    <CircularProgress aria-label='Loading...' size='sm' />
  ) : (
    <StandardIcon id='mdi:search' />
  );

  return (
    <div
      className={`flex flex-col items-end sm:min-w-[360px] md:items-center ${className} ${extraClassNames}`}
    >
      <div className='flex w-full flex-row items-center'>
        <Input
          className={`${inputClassName} hidden sm:block`}
          placeholder='Search by name, email or phone...'
          endContent={<StandardIcon id='mdi:search' />}
          isClearable
          isReadOnly
          onClick={onButtonPress}
          autoComplete='off'
          autoCorrect='off'
          startContent={<StandardIcon id='mdi:search' />}
        />
        <div className='flex-grow md:hidden' />
        <Button
          className='sm:hidden'
          variant='light'
          isIconOnly
          onPress={onButtonPress}
        >
          <StandardIcon id='mdi:search' size={24} />
        </Button>
      </div>
      <Popover
        isOpen={open}
        onOpenChange={(value) => setOpen(value)}
        placement='bottom'
        offset={-48}
        shouldCloseOnScroll={false}
      >
        <PopoverTrigger>
          <div className='w-full' ref={trigger} />
        </PopoverTrigger>
        <PopoverContent>
          <div className='flex w-[calc(100vw-50px)] flex-col items-center p-2 sm:w-[420px]'>
            <div className='flex w-full flex-row'>
              <Input
                value={searchInput}
                onValueChange={setSearchInput}
                className={`${inputClassName} mb-4 mr-2 flex-grow`}
                placeholder='Search by name, email or phone...'
                endContent={searchInput ? undefined : endContent}
                isClearable
                autoFocus
                autoComplete='off'
                autoCorrect='off'
                startContent={<StandardIcon id='mdi:search' />}
              />
              <Button isIconOnly variant='light' onPress={onClosePress}>
                <StandardIcon id='mdi:close' />
              </Button>
            </div>
            <div className='flex min-h-32 w-full min-w-72 flex-col items-center'>
              <Content
                {...{
                  loading,
                  empty,
                  searchInput,
                  people,
                  onAction,
                  onPersonClick,
                }}
              />
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function Content({
  loading,
  empty,
  searchInput,
  people,
  onAction,
  onPersonClick,
}: {
  loading: boolean;
  empty: boolean;
  searchInput: string;
  people: Person[];
  onAction: (key: ActionKey, item: Person) => void;
  onPersonClick: (item: Person) => void;
}) {
  if (loading) {
    return <CircularProgress className='mt-4 self-center' />;
  }

  if (!searchInput) {
    return (
      <Empty
        description='Search results will be shown here'
        icon='mdi:search'
      />
    );
  }

  if (empty) {
    return (
      <div className='w-full'>
        <Empty description='No results' icon='mdi:search' />
      </div>
    );
  }

  return (
    <div className='max-h-auto flex w-full flex-col overflow-y-auto sm:max-h-[30vh] md:mt-0'>
      {people.map((item) => (
        <div
          className='mt-1 flex flex-row items-center rounded-lg bg-default-100 p-2'
          key={item.id}
        >
          <PersonItem
            item={item}
            className='flex-grow cursor-pointer'
            onPress={() => onPersonClick(item)}
            nameEndContent={
              item.data.member_status ? (
                <MemberStatusChip id={item.data.member_status} />
              ) : null
            }
          />
          <PersonActions item={item} onAction={onAction} />
        </div>
      ))}
    </div>
  );
}

function PersonActions({
  item,
  onAction,
}: {
  item: Person;
  onAction: (key: ActionKey, item: Person) => void;
}) {
  const access = useSpecificPeopleAccess(item);

  const availableActions = actions.filter((action) => {
    if (action.key === 'comment' && access.access.comment_create !== 'allow') {
      return false;
    }

    return true;
  });

  return (
    <Dropdown>
      <DropdownTrigger>
        <Button isIconOnly variant='light' className='ml-2'>
          <StandardIcon id='mdi:dots-vertical' />
        </Button>
      </DropdownTrigger>

      <DropdownMenu items={availableActions}>
        {(action) => (
          <DropdownItem
            key={action.key}
            onPress={() => onAction(action.key, item)}
          >
            <div className='flex flex-row items-center gap-x-2'>
              <StandardIcon id={action.icon} />
              <span>{action.label}</span>
            </div>
          </DropdownItem>
        )}
      </DropdownMenu>
    </Dropdown>
  );
}
